import React, { useEffect, useMemo, useState } from 'react';
import { usePolling, useApi } from './hooks/useApi';
import {
  fetchDashboard,
  fetchCriticality,
  fetchIssues,
  triggerSync,
  fetchSyncStatus,
  fetchProjects,
  updateProjectVisibility,
} from './utils/api';
import {
  AlertTriangle,
  Clock,
  Users,
  BarChart3,
  RefreshCw,
  ArrowUpRight,
  CircleDot,
  Zap,
  Shield,
  Eye,
  EyeOff,
  ListTodo,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Menu,
  X,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import ProjectAutocomplete from './components/ProjectAutocomplete';
import ProjectManagerPanel from './components/ProjectManagerPanel';
import './styles/global.css';

const PROJECTS_PER_PAGE = 10;

const NAV_ITEMS = [
  { id: 'overview', label: 'Dashboard', subtitle: 'Saúde dos projetos', icon: BarChart3 },
  { id: 'criticality', label: 'Criticidade', subtitle: 'Priorização operacional', icon: Zap },
  { id: 'workload', label: 'Carga por Dev', subtitle: 'Distribuição da equipe', icon: Users },
  { id: 'projects', label: 'Projetos', subtitle: 'Ocultar e reexibir', icon: FolderOpen },
  { id: 'issues', label: 'Issues', subtitle: 'Lista por projeto', icon: ListTodo },
];

const VALID_TABS = NAV_ITEMS.map((item) => item.id);
const ISSUE_SORT_ACCESSORS = {
  jira_key: (item) => item.jira_key,
  status: (item) => item.status,
  assignee_name: (item) => item.assignee_name || 'Sem responsável',
};
const CRITICALITY_SORT_ACCESSORS = {
  issue_key: (item) => item.issue_key,
  status: (item) => item.status,
  assignee_name: (item) => item.assignee_name,
};

function getStatColor(color) {
  if (color === 'cyan') return 'accent-blue';
  if (color === 'red') return 'status-critical';
  if (color === 'yellow') return 'status-warning';
  if (color === 'blue') return 'accent-blue-dark';
  return 'status-info';
}

function getPriorityBadgeClass(priority) {
  if (priority === 'Highest' || priority === 'Blocker') return 'badge-critical';
  if (priority === 'High' || priority === 'Critical') return 'badge-high';
  if (priority === 'Low' || priority === 'Lowest') return 'badge-low';
  return 'badge-medium';
}

function formatProjectOption(project) {
  return {
    jira_key: project.jira_key,
    name: project.name || project.project_name,
    client_name: project.client_name || '',
  };
}

function normalizeText(value) {
  return String(value || '').trim();
}

function compareLocale(a, b) {
  return normalizeText(a).localeCompare(normalizeText(b), 'pt-BR', {
    numeric: true,
    sensitivity: 'base',
  });
}

function sortCollection(items, sortConfig, accessors) {
  if (!sortConfig) return items;

  const rows = [...items];
  rows.sort((left, right) => {
    const leftValue = accessors[sortConfig.key](left);
    const rightValue = accessors[sortConfig.key](right);
    const comparison = compareLocale(leftValue, rightValue);
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  return rows;
}

function getNextSort(prevSort, key) {
  if (!prevSort || prevSort.key !== key) {
    return { key, direction: 'asc' };
  }

  return {
    key,
    direction: prevSort.direction === 'asc' ? 'desc' : 'asc',
  };
}

function parsePositiveInt(value, fallback = 1) {
  const parsed = Number.parseInt(value || '', 10);
  if (Number.isNaN(parsed) || parsed < 1) return fallback;
  return parsed;
}

function parseSortFromParams(params, keyParam, dirParam, allowedKeys) {
  const key = params.get(keyParam);
  if (!key || !allowedKeys.includes(key)) return null;

  const direction = params.get(dirParam) === 'desc' ? 'desc' : 'asc';
  return { key, direction };
}

function readUrlState() {
  if (typeof window === 'undefined') {
    return {
      activeTab: 'overview',
      selectedProjectKey: '',
      projectPage: 1,
      managerSearch: '',
      issueSort: null,
      criticalitySort: null,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const activeTab = params.get('tab');

  return {
    activeTab: VALID_TABS.includes(activeTab) ? activeTab : 'overview',
    selectedProjectKey: params.get('project') || '',
    projectPage: parsePositiveInt(params.get('page'), 1),
    managerSearch: params.get('projectSearch') || '',
    issueSort: parseSortFromParams(params, 'issueSort', 'issueDir', Object.keys(ISSUE_SORT_ACCESSORS)),
    criticalitySort: parseSortFromParams(params, 'criticalitySort', 'criticalityDir', Object.keys(CRITICALITY_SORT_ACCESSORS)),
  };
}

function syncUrlState({
  activeTab,
  selectedProjectKey,
  projectPage,
  managerSearch,
  issueSort,
  criticalitySort,
}) {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams();

  if (activeTab && activeTab !== 'overview') params.set('tab', activeTab);
  if (selectedProjectKey) params.set('project', selectedProjectKey);
  if (projectPage > 1) params.set('page', String(projectPage));
  if (activeTab === 'projects' && managerSearch.trim()) params.set('projectSearch', managerSearch.trim());

  if (issueSort) {
    params.set('issueSort', issueSort.key);
    params.set('issueDir', issueSort.direction);
  }

  if (criticalitySort) {
    params.set('criticalitySort', criticalitySort.key);
    params.set('criticalityDir', criticalitySort.direction);
  }

  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
  const currentUrl = `${window.location.pathname}${window.location.search}`;

  if (nextUrl !== currentUrl) {
    window.history.replaceState(null, '', nextUrl);
  }
}

function SortHeader({ label, active, direction, onClick }) {
  return (
    <button
      type="button"
      className={`table-sort-button ${active ? 'active' : ''}`}
      onClick={onClick}
    >
      <span>{label}</span>
      {active ? (
        direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
      ) : (
        <ArrowUpDown size={14} />
      )}
    </button>
  );
}

function SidebarNavItem({ item, active, onClick }) {
  const Icon = item.icon;

  return (
    <button
      type="button"
      className={`sidebar-item ${active ? 'active' : ''}`}
      onClick={() => onClick(item.id)}
    >
      <span className="sidebar-item-icon"><Icon size={18} /></span>
      <span className="sidebar-item-copy">
        <span className="sidebar-item-label">{item.label}</span>
        <span className="sidebar-item-subtitle">{item.subtitle}</span>
      </span>
    </button>
  );
}

function StatCard({ value, label, color, icon: Icon, delay }) {
  return (
    <div className={`card stat-card ${color} fade-in fade-in-${delay}`}>
      <div className="stat-card-content">
        <div>
          <div className="stat-value" style={{ color: `var(--${getStatColor(color)})` }}>
            {value}
          </div>
          <div className="stat-label">{label}</div>
        </div>
        {Icon && <Icon size={20} style={{ color: 'var(--text-muted)', opacity: 0.8 }} />}
      </div>
    </div>
  );
}

function HealthBadge({ critical, overdue, stale, total }) {
  const totalOpen = total || 1;
  const ratio = (critical * 2 + overdue * 1.5 + stale) / totalOpen;
  const health = ratio > 0.5 ? 'red' : ratio > 0.2 ? 'yellow' : 'green';
  const labels = { green: 'Saudável', yellow: 'Atenção', red: 'Crítico' };

  return (
    <span className={`health-indicator health-${health}`}>
      <CircleDot size={10} /> {labels[health]}
    </span>
  );
}

function ProjectCard({ project, onOpenIssues, onHideProject, isBusy }) {
  const totalOpen = project.todo_count + project.in_progress_count;

  return (
    <div className={`card project-card ${project.critical_count > 2 ? 'health-red' : ''}`}>
      <div className="card-header">
        <div>
          <div className="project-name">{project.project_name}</div>
          <div className="project-key">{project.jira_key} {project.client_name && `· ${project.client_name}`}</div>
        </div>
        <HealthBadge
          critical={project.critical_count}
          overdue={project.overdue_count}
          stale={project.stale_count}
          total={totalOpen}
        />
      </div>

      <div className="project-stats">
        <div className="project-stat">
          <div className="project-stat-value" style={{ color: 'var(--text-secondary)' }}>{project.todo_count}</div>
          <div className="project-stat-label">To Do</div>
        </div>
        <div className="project-stat">
          <div className="project-stat-value" style={{ color: 'var(--accent-blue)' }}>{project.in_progress_count}</div>
          <div className="project-stat-label">In Progress</div>
        </div>
        <div className="project-stat">
          <div className="project-stat-value" style={{ color: 'var(--status-success)' }}>{project.done_count}</div>
          <div className="project-stat-label">Done</div>
        </div>
      </div>

      <div className="project-card-footer">
        <div className="project-card-badges">
          {project.critical_count > 0 && <span className="badge badge-critical">{project.critical_count} críticos</span>}
          {project.overdue_count > 0 && <span className="badge badge-overdue">{project.overdue_count} atrasados</span>}
          {project.stale_count > 0 && <span className="badge badge-high">{project.stale_count} parados &gt;7d</span>}
        </div>

        <div className="project-card-actions">
          <button type="button" className="btn btn-primary" onClick={() => onOpenIssues(project.jira_key)} disabled={isBusy}>
            <ListTodo size={14} />
            Ver issues
          </button>
          <button type="button" className="btn btn-danger" onClick={() => onHideProject(project.jira_key)} disabled={isBusy}>
            <EyeOff size={14} />
            {isBusy ? 'Salvando...' : 'Ocultar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CriticalityRow({ item, rank }) {
  const scoreColor =
    item.score >= 70 ? 'var(--status-critical)' :
    item.score >= 40 ? 'var(--status-warning)' :
    'var(--status-success)';

  return (
    <tr>
      <td className="table-rank">#{rank}</td>
      <td>
        <div className="table-primary">{item.issue_key}</div>
        <div className="table-secondary table-truncate">{item.summary}</div>
      </td>
      <td>
        <span className="table-primary">{item.project_name}</span>
        <span className="table-meta"> {item.project_key}</span>
      </td>
      <td><span className={`badge ${getPriorityBadgeClass(item.priority)}`}>{item.priority}</span></td>
      <td className="table-mono">{item.assignee_name}</td>
      <td className="table-mono">{item.status}</td>
      <td className="table-mono">{Math.round(item.age_days)}d</td>
      <td className="table-mono" style={{ color: item.days_in_status > 7 ? 'var(--status-warning)' : 'inherit' }}>
        {Math.round(item.days_in_status)}d
      </td>
      <td>
        <div className="score-bar">
          <div className="score-bar-track">
            <div
              className="score-bar-fill"
              style={{ width: `${item.score}%`, background: scoreColor }}
            />
          </div>
          <span className="score-value" style={{ color: scoreColor }}>
            {Math.round(item.score)}
          </span>
        </div>
      </td>
    </tr>
  );
}

function WorkloadCard({ member }) {
  const initials = member.display_name.split(' ').map((name) => name[0]).join('').slice(0, 2);
  const status =
    member.open_issues > 10 || member.critical_issues > 3 ? { label: 'Sobrecarregado', color: 'var(--status-critical)' } :
    member.open_issues > 7 || member.critical_issues > 2 ? { label: 'Carga alta', color: 'var(--status-warning)' } :
    member.open_issues > 3 ? { label: 'Normal', color: 'var(--status-success)' } :
    { label: 'Disponível', color: 'var(--accent-blue)' };

  return (
    <div className="card workload-card">
      <div className="workload-avatar">{initials}</div>
      <div className="workload-info">
        <div className="workload-name">{member.display_name}</div>
        <div className="workload-detail">
          {member.open_issues} abertos · {member.in_progress} em progresso · {member.critical_issues} críticos
        </div>
        <div style={{ fontSize: '0.74rem', color: status.color, fontWeight: 600, marginTop: 4 }}>
          {status.label}
        </div>
      </div>
    </div>
  );
}

function IssueRow({ issue }) {
  const priority = issue.priority || 'Medium';

  return (
    <tr>
      <td><div className="table-primary">{issue.jira_key}</div></td>
      <td><div className="table-primary">{issue.summary}</div></td>
      <td><span className={`badge ${getPriorityBadgeClass(priority)}`}>{priority}</span></td>
      <td className="table-mono">{issue.status}</td>
      <td className="table-mono">{issue.assignee_name || 'Sem responsável'}</td>
      <td className="table-mono">{Math.round(issue.age_days)}d</td>
      <td className="table-mono">{Math.round(issue.days_in_status)}d</td>
      <td>
        {issue.is_overdue ? (
          <span className="badge badge-overdue">Atrasada</span>
        ) : (
          <span className="badge badge-low">No prazo</span>
        )}
      </td>
    </tr>
  );
}

function PaginationControls({ currentPage, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  return (
    <div className="pagination">
      <button
        type="button"
        className="btn"
        disabled={currentPage === 1}
        onClick={() => onChange(currentPage - 1)}
      >
        <ChevronLeft size={14} />
        Anterior
      </button>

      <div className="pagination-status">
        Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
      </div>

      <button
        type="button"
        className="btn"
        disabled={currentPage === totalPages}
        onClick={() => onChange(currentPage + 1)}
      >
        Próxima
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

function EmptyState({ title, description, actionLabel, onAction }) {
  return (
    <div className="card empty-state">
      <div className="empty-state-title">{title}</div>
      <div className="empty-state-description">{description}</div>
      {actionLabel && onAction && (
        <button type="button" className="btn btn-primary" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="loading-state">
      <div className="stats-grid">
        {[1, 2, 3, 4, 5].map((item) => (
          <div key={item} className="skeleton" style={{ height: 110, borderRadius: 'var(--radius-lg)' }} />
        ))}
      </div>
      <div className="projects-grid" style={{ marginTop: 24 }}>
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="skeleton" style={{ height: 210, borderRadius: 'var(--radius-lg)' }} />
        ))}
      </div>
    </div>
  );
}

export default function App() {
  const initialUrlState = useMemo(() => readUrlState(), []);

  const [activeTab, setActiveTab] = useState(initialUrlState.activeTab);
  const [selectedProjectKey, setSelectedProjectKey] = useState(initialUrlState.selectedProjectKey);
  const [syncing, setSyncing] = useState(false);
  const [projectPage, setProjectPage] = useState(initialUrlState.projectPage);
  const [managerSearch, setManagerSearch] = useState(initialUrlState.managerSearch);
  const [savingProjectKey, setSavingProjectKey] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [issueSort, setIssueSort] = useState(initialUrlState.issueSort);
  const [criticalitySort, setCriticalitySort] = useState(initialUrlState.criticalitySort);

  const { data: dashboard, loading: dashLoading, refetch: refetchDash } = usePolling(fetchDashboard, 60000);
  const { data: criticality, refetch: refetchCrit } = useApi(fetchCriticality);
  const { data: syncStatus } = usePolling(fetchSyncStatus, 30000);
  const { data: projectsData, refetch: refetchProjects } = usePolling(fetchProjects, 60000);
  const { data: issuesData, loading: issuesLoading, refetch: refetchIssues } = useApi(
    () => (selectedProjectKey ? fetchIssues({ project: selectedProjectKey }) : Promise.resolve([])),
    [selectedProjectKey]
  );

  const d = dashboard || {};
  const issues = Array.isArray(issuesData) ? issuesData : [];
  const projectsCatalog = useMemo(
    () => (Array.isArray(projectsData) ? projectsData : []),
    [projectsData]
  );

  const autocompleteProjects = useMemo(() => {
    if (projectsCatalog.length > 0) {
      return projectsCatalog
        .filter((project) => project.is_visible)
        .map(formatProjectOption);
    }

    return (d.project_health || []).map((project) => ({
      jira_key: project.jira_key,
      name: project.project_name,
      client_name: project.client_name || '',
    }));
  }, [d.project_health, projectsCatalog]);

  const selectedProject = useMemo(
    () => projectsCatalog.find((project) => project.jira_key === selectedProjectKey) || null,
    [projectsCatalog, selectedProjectKey]
  );

  const filteredCriticality = useMemo(() => {
    if (!criticality) return [];
    if (!selectedProjectKey) return criticality;
    return criticality.filter((item) => item.project_key === selectedProjectKey);
  }, [criticality, selectedProjectKey]);

  const sortedCriticality = useMemo(
    () => sortCollection(filteredCriticality, criticalitySort, CRITICALITY_SORT_ACCESSORS),
    [criticalitySort, filteredCriticality]
  );

  const filteredProjects = useMemo(() => {
    if (!dashboard?.project_health) return [];
    if (!selectedProjectKey) return dashboard.project_health;
    return dashboard.project_health.filter((project) => project.jira_key === selectedProjectKey);
  }, [dashboard, selectedProjectKey]);

  const paginatedProjects = useMemo(() => {
    const start = (projectPage - 1) * PROJECTS_PER_PAGE;
    return filteredProjects.slice(start, start + PROJECTS_PER_PAGE);
  }, [filteredProjects, projectPage]);

  const totalProjectPages = Math.max(1, Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE));

  const filteredManagerProjects = useMemo(() => {
    if (!managerSearch.trim()) return projectsCatalog;

    const term = managerSearch.trim().toLowerCase();
    return projectsCatalog.filter((project) => {
      const haystack = `${project.name} ${project.jira_key} ${project.client_name || ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [managerSearch, projectsCatalog]);

  const sortedIssues = useMemo(
    () => sortCollection(issues, issueSort, ISSUE_SORT_ACCESSORS),
    [issueSort, issues]
  );

  const syncTimeAgo = useMemo(() => {
    if (syncing || syncStatus?.status === 'running') return 'Sincronizando agora';
    if (syncStatus?.status === 'error') return 'Falha na última sync';
    if (!syncStatus?.finished_at) return 'Nunca sincronizado';

    const diff = Date.now() - new Date(syncStatus.finished_at).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Agora mesmo';
    if (mins < 60) return `${mins}min atrás`;
    return `${Math.floor(mins / 60)}h ${mins % 60}min atrás`;
  }, [syncStatus, syncing]);

  useEffect(() => {
    setProjectPage(1);
  }, [selectedProjectKey]);

  useEffect(() => {
    if (projectPage <= totalProjectPages) return;
    setProjectPage(totalProjectPages);
  }, [projectPage, totalProjectPages]);

  useEffect(() => {
    if (!selectedProjectKey || projectsCatalog.length === 0 || selectedProject?.is_visible) return;

    setSelectedProjectKey('');
    if (activeTab === 'issues') {
      setActiveTab('projects');
    }
  }, [activeTab, projectsCatalog.length, selectedProject, selectedProjectKey]);

  useEffect(() => {
    const handlePopState = () => {
      const nextState = readUrlState();
      setActiveTab(nextState.activeTab);
      setSelectedProjectKey(nextState.selectedProjectKey);
      setProjectPage(nextState.projectPage);
      setManagerSearch(nextState.managerSearch);
      setIssueSort(nextState.issueSort);
      setCriticalitySort(nextState.criticalitySort);
      setMobileNavOpen(false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    syncUrlState({
      activeTab,
      selectedProjectKey,
      projectPage,
      managerSearch,
      issueSort,
      criticalitySort,
    });
  }, [activeTab, selectedProjectKey, projectPage, managerSearch, issueSort, criticalitySort]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMobileNavOpen(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await triggerSync();
      setTimeout(() => {
        refetchDash();
        refetchCrit();
        refetchProjects();
        if (selectedProjectKey) {
          refetchIssues();
        }
        setSyncing(false);
      }, 5000);
    } catch {
      setSyncing(false);
    }
  };

  const handleProjectVisibility = async (projectKey, isVisible) => {
    setSavingProjectKey(projectKey);

    const shouldClearSelection = !isVisible && selectedProjectKey === projectKey;
    const shouldRefetchIssues = Boolean(selectedProjectKey) && !shouldClearSelection;

    try {
      await updateProjectVisibility(projectKey, isVisible);

      if (shouldClearSelection) {
        setSelectedProjectKey('');
        if (activeTab === 'issues') {
          setActiveTab('projects');
        }
      }

      await Promise.all([refetchProjects(), refetchDash(), refetchCrit()]);
      if (shouldRefetchIssues) {
        await refetchIssues();
      }
    } finally {
      setSavingProjectKey('');
    }
  };

  const openProjectIssues = (projectKey) => {
    setSelectedProjectKey(projectKey);
    setActiveTab('issues');
    setMobileNavOpen(false);
  };

  if (dashLoading && !dashboard) return <LoadingSkeleton />;

  return (
    <div className="shell">
      <header className="header">
        <div className="header-brand">
          <button
            type="button"
            className="nav-toggle"
            aria-label={mobileNavOpen ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
          </button>

          <img
            className="brand-logo"
            src="/logo_branca.png"
            alt="SuperBoard"
          />

          <div className="brand-copy">
            <div className="brand-title">SuperBoard</div>
            <div className="brand-subtitle">Painel executivo de projetos Jira</div>
          </div>
        </div>

        <div className="header-actions">
          <div className="sync-indicator">
            <div className={`sync-dot ${syncing ? 'syncing' : ''}`} />
            {syncTimeAgo}
          </div>

          <button className="btn btn-topbar" onClick={handleSync} disabled={syncing}>
            <RefreshCw size={14} className={syncing ? 'spinning' : ''} />
            {syncing ? 'Sincronizando...' : 'Sync Jira'}
          </button>
        </div>
      </header>

      <div className="shell-body">
        <button
          type="button"
          className={`sidebar-backdrop ${mobileNavOpen ? 'visible' : ''}`}
          aria-label="Fechar menu"
          onClick={() => setMobileNavOpen(false)}
        />

        <aside className={`sidebar ${mobileNavOpen ? 'open' : ''}`}>
          <div className="sidebar-section-label">Centro de Controle</div>
          <nav className="sidebar-nav">
            {NAV_ITEMS.map((item) => (
              <SidebarNavItem
                key={item.id}
                item={item}
                active={activeTab === item.id}
                onClick={handleTabChange}
              />
            ))}
          </nav>
        </aside>

        <main className="main-content">
          <div className="page-heading">
            <div>
              <h1 className="page-title">Operação consolidada do Jira</h1>
              <p className="page-description">
                Mesmo padrão visual do Financeiro Ideale, agora aplicado ao painel de projetos.
              </p>
            </div>
          </div>

          <div className="stats-grid">
            <StatCard value={d.total_projects || 0} label="Projetos visíveis" color="cyan" icon={BarChart3} delay={1} />
            <StatCard value={d.total_open_issues || 0} label="Issues abertas" color="blue" icon={Eye} delay={2} />
            <StatCard value={d.critical_issues || 0} label="Críticas" color="red" icon={AlertTriangle} delay={3} />
            <StatCard value={d.overdue_issues || 0} label="Atrasadas" color="yellow" icon={Clock} delay={4} />
            <StatCard value={d.stale_issues || 0} label="Paradas >7d" color="purple" icon={Shield} delay={4} />
          </div>

          <div className="card toolbar-panel">
            <div className="filter-bar">
              <div className="filter-group">
                <span className="filter-label">Todos os Projetos</span>
                <ProjectAutocomplete
                  projects={autocompleteProjects}
                  selectedProjectKey={selectedProjectKey}
                  onSelectProject={setSelectedProjectKey}
                />
              </div>

              {selectedProjectKey && (
                <button type="button" className="btn" onClick={() => setSelectedProjectKey('')}>
                  Limpar filtro
                </button>
              )}
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="fade-in">
              <div className="section-header">
                <div>
                  <h2 className="section-title">Saúde dos Projetos</h2>
                  <div className="section-subtitle">
                    {filteredProjects.length} projeto(s) visível(is) no painel
                  </div>
                </div>
              </div>

              {filteredProjects.length > 0 ? (
                <>
                  <div className="projects-grid">
                    {paginatedProjects.map((project) => (
                      <ProjectCard
                        key={project.jira_key}
                        project={project}
                        onOpenIssues={openProjectIssues}
                        onHideProject={(projectKey) => handleProjectVisibility(projectKey, false)}
                        isBusy={savingProjectKey === project.jira_key}
                      />
                    ))}
                  </div>

                  <PaginationControls
                    currentPage={projectPage}
                    totalPages={totalProjectPages}
                    onChange={setProjectPage}
                  />
                </>
              ) : (
                <EmptyState
                  title="Nenhum projeto encontrado"
                  description="Ajuste o filtro acima ou reexiba o projeto na aba Projetos."
                  actionLabel="Gerenciar projetos"
                  onAction={() => handleTabChange('projects')}
                />
              )}

              {sortedCriticality.length > 0 && (
                <>
                  <div className="section-header section-header-spaced">
                    <h2 className="section-title">Top 5 Issues Mais Críticas</h2>
                    <button className="btn" onClick={() => handleTabChange('criticality')}>
                      Ver tudo <ArrowUpRight size={14} />
                    </button>
                  </div>

                  <div className="card table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>
                            <SortHeader
                              label="Issue"
                              active={criticalitySort?.key === 'issue_key'}
                              direction={criticalitySort?.direction}
                              onClick={() => setCriticalitySort((prev) => getNextSort(prev, 'issue_key'))}
                            />
                          </th>
                          <th>Projeto</th>
                          <th>Prior.</th>
                          <th>
                            <SortHeader
                              label="Responsável"
                              active={criticalitySort?.key === 'assignee_name'}
                              direction={criticalitySort?.direction}
                              onClick={() => setCriticalitySort((prev) => getNextSort(prev, 'assignee_name'))}
                            />
                          </th>
                          <th>
                            <SortHeader
                              label="Status"
                              active={criticalitySort?.key === 'status'}
                              direction={criticalitySort?.direction}
                              onClick={() => setCriticalitySort((prev) => getNextSort(prev, 'status'))}
                            />
                          </th>
                          <th>Idade</th>
                          <th>Parado</th>
                          <th>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCriticality.slice(0, 5).map((item, index) => (
                          <CriticalityRow key={item.issue_key} item={item} rank={index + 1} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'criticality' && (
            <div className="fade-in">
              <div className="section-header">
                <div>
                  <h2 className="section-title">Ranking de Criticidade</h2>
                  <div className="section-subtitle">
                    {sortedCriticality.length} issue(s) abertas com score calculado
                  </div>
                </div>
              </div>

              {sortedCriticality.length > 0 ? (
                <div className="card table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>
                          <SortHeader
                            label="Issue"
                            active={criticalitySort?.key === 'issue_key'}
                            direction={criticalitySort?.direction}
                            onClick={() => setCriticalitySort((prev) => getNextSort(prev, 'issue_key'))}
                          />
                        </th>
                        <th>Projeto</th>
                        <th>Prior.</th>
                        <th>
                          <SortHeader
                            label="Responsável"
                            active={criticalitySort?.key === 'assignee_name'}
                            direction={criticalitySort?.direction}
                            onClick={() => setCriticalitySort((prev) => getNextSort(prev, 'assignee_name'))}
                          />
                        </th>
                        <th>
                          <SortHeader
                            label="Status"
                            active={criticalitySort?.key === 'status'}
                            direction={criticalitySort?.direction}
                            onClick={() => setCriticalitySort((prev) => getNextSort(prev, 'status'))}
                          />
                        </th>
                        <th>Idade</th>
                        <th>Parado</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCriticality.map((item, index) => (
                        <CriticalityRow key={item.issue_key} item={item} rank={index + 1} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  title="Sem issues críticas para mostrar"
                  description="Esse filtro não retornou issues abertas com score de criticidade."
                />
              )}
            </div>
          )}

          {activeTab === 'workload' && (
            <div className="fade-in">
              <div className="section-header">
                <div>
                  <h2 className="section-title">Distribuição de Carga</h2>
                  <div className="section-subtitle">
                    {(d.member_workloads || []).length} pessoa(s) com atividade nos projetos visíveis
                  </div>
                </div>
              </div>

              <div className="workload-grid">
                {(d.member_workloads || []).map((member) => (
                  <WorkloadCard key={member.member_id} member={member} />
                ))}
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <ProjectManagerPanel
              allProjects={projectsCatalog}
              projects={filteredManagerProjects}
              searchValue={managerSearch}
              onSearchChange={setManagerSearch}
              savingProjectKey={savingProjectKey}
              selectedProjectKey={selectedProjectKey}
              onToggleVisibility={handleProjectVisibility}
              onOpenIssues={openProjectIssues}
            />
          )}

          {activeTab === 'issues' && (
            <div className="fade-in">
              <div className="section-header">
                <div>
                  <h2 className="section-title">Issues do Projeto</h2>
                  <div className="section-subtitle">
                    {selectedProject ? `${selectedProject.name} (${selectedProject.jira_key})` : 'Selecione um projeto para ver as issues abertas'}
                  </div>
                </div>

                {selectedProjectKey && selectedProject?.is_visible && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={savingProjectKey === selectedProjectKey}
                    onClick={() => handleProjectVisibility(selectedProjectKey, false)}
                  >
                    <EyeOff size={14} />
                    Ocultar projeto
                  </button>
                )}
              </div>

              {!selectedProjectKey ? (
                <EmptyState
                  title="Nenhum projeto selecionado"
                  description="Escolha um projeto no autocomplete acima ou abra a lista a partir de um card."
                  actionLabel="Voltar para dashboard"
                  onAction={() => handleTabChange('overview')}
                />
              ) : issuesLoading && !issuesData ? (
                <div className="card empty-state">Carregando issues do projeto...</div>
              ) : sortedIssues.length > 0 ? (
                <div className="card table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>
                          <SortHeader
                            label="Issue"
                            active={issueSort?.key === 'jira_key'}
                            direction={issueSort?.direction}
                            onClick={() => setIssueSort((prev) => getNextSort(prev, 'jira_key'))}
                          />
                        </th>
                        <th>Resumo</th>
                        <th>Prior.</th>
                        <th>
                          <SortHeader
                            label="Status"
                            active={issueSort?.key === 'status'}
                            direction={issueSort?.direction}
                            onClick={() => setIssueSort((prev) => getNextSort(prev, 'status'))}
                          />
                        </th>
                        <th>
                          <SortHeader
                            label="Responsável"
                            active={issueSort?.key === 'assignee_name'}
                            direction={issueSort?.direction}
                            onClick={() => setIssueSort((prev) => getNextSort(prev, 'assignee_name'))}
                          />
                        </th>
                        <th>Idade</th>
                        <th>Parado</th>
                        <th>Prazo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedIssues.map((issue) => (
                        <IssueRow key={issue.jira_id} issue={issue} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  title="Nenhuma issue aberta nesse projeto"
                  description="Esse endpoint mostra as issues abertas; se tudo estiver concluído, a lista fica vazia."
                />
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
